import { useState, type ReactNode } from "react";

import type { ClubView, MatchSummary, PlayerRow } from "../game/types";
import { Crest, Money, MoraleFace, Panel, PositionChip, StrengthBadge } from "../ui/atoms";
import { fatorDaPartida, lanceDaPartida, rotuloDeFase, useT, type Chave } from "../i18n";
import { tabelaDaCompeticao } from "./CompetitionsPanel";
import { StandingsTable } from "./panels";

type Aba = "posJogo" | "rodada" | "elenco" | "financeiro";

/**
 * Relatórios da temporada, num lugar só: o último jogo, a rodada, o elenco e o
 * dinheiro.
 *
 * Nada aqui é dado novo do motor. É a mesma informação que já existe espalhada
 * (o resumo da partida, a tabela, a artilharia, a folha), recortada do jeito
 * que se lê depois de uma rodada: o que aconteceu, quem foi bem, quem preocupa
 * e quanto entrou e saiu.
 */
export function RelatoriosPanel({
  view,
  ultimoJogo,
  onOpenPlayer,
}: {
  view: ClubView;
  /** O resumo da última partida jogada nesta sessão, se houver. */
  ultimoJogo: MatchSummary | null;
  onOpenPlayer: (playerId: string) => void;
}): ReactNode {
  const t = useT();
  const [aba, setAba] = useState<Aba>("posJogo");
  const abas: Array<[Aba, Chave]> = [
    ["posJogo", "relatorios.abas.posJogo"],
    ["rodada", "relatorios.abas.rodada"],
    ["elenco", "relatorios.abas.elenco"],
    ["financeiro", "relatorios.abas.financeiro"],
  ];

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="row" role="tablist" style={{ gap: 8, flexWrap: "wrap" }}>
        {abas.map(([chave, rotulo]) => (
          <button
            key={chave}
            role="tab"
            aria-selected={aba === chave}
            className="ghost"
            onClick={() => setAba(chave)}
            style={aba === chave ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 } : undefined}
          >
            {t(rotulo)}
          </button>
        ))}
      </div>
      {aba === "posJogo" && <PosJogo view={view} jogo={ultimoJogo} />}
      {aba === "rodada" && <Rodada view={view} onOpenPlayer={onOpenPlayer} />}
      {aba === "elenco" && <Elenco view={view} onOpenPlayer={onOpenPlayer} />}
      {aba === "financeiro" && <Financeiro view={view} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pós-jogo
// ---------------------------------------------------------------------------

function PosJogo({ view, jogo }: { view: ClubView; jogo: MatchSummary | null }): ReactNode {
  const t = useT();
  if (!jogo) {
    // Sem o resumo desta sessão, ao menos o placar do último jogo do calendário.
    const ultimo = [...view.calendar].reverse().find((linha) => linha.played) ?? null;
    return (
      <Panel title={t("relatorios.abas.posJogo")}>
        {ultimo ? (
          <div className="row" style={{ gap: 10 }}>
            <Crest colors={ultimo.opponentColors} short={ultimo.opponentShort} size={26} />
            <strong>{ultimo.opponent}</strong>
            <span className="num" style={{ fontWeight: 700 }}>
              {ultimo.goalsFor} x {ultimo.goalsAgainst}
            </span>
            <span className="label-muted">
              {ultimo.competition} · {ultimo.home ? t("hub.emCasa") : t("hub.foraDeCasa")}
            </span>
          </div>
        ) : (
          <p className="muted" style={{ margin: 0 }}>
            {t("relatorios.semJogo")}
          </p>
        )}
      </Panel>
    );
  }

  const estatisticas: Array<[Chave, number, number, string]> = [
    ["relatorios.posse", jogo.possession, 100 - jogo.possession, "%"],
    ["relatorios.chances", jogo.chances, jogo.opponentChances, ""],
  ];
  const meuLado = jogo.events.find((evento) => evento.mine)?.clubShort === jogo.awayShort ? "fora" : "casa";
  const [minhas, deles] = meuLado === "casa" ? [0, 1] : [1, 0];

  return (
    <div className="relatorios-duas">
      <Panel title={t("relatorios.abas.posJogo")}>
        <div className="row" style={{ gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
          <Crest colors={jogo.homeColors} short={jogo.homeShort} size={28} />
          <strong>{jogo.homeName}</strong>
          <span className="num" style={{ fontSize: 22, fontWeight: 800 }}>
            {jogo.homeGoals} x {jogo.awayGoals}
          </span>
          <strong>{jogo.awayName}</strong>
          <Crest colors={jogo.awayColors} short={jogo.awayShort} size={28} />
        </div>
        <div className="label-muted" style={{ marginBottom: 14 }}>
          {jogo.competitionName}
          {jogo.stage && jogo.stage !== "liga" && jogo.stage !== "estadual" ? ` · ${rotuloDeFase(jogo.stage)}` : ""}
        </div>
        {jogo.events.length > 0 && (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {jogo.events.map((evento, indice) => (
              <li key={indice} className="row" style={{ gap: 9, padding: "3px 0", opacity: evento.mine ? 1 : 0.72 }}>
                <span className="num" style={{ minWidth: 28, color: "var(--text-dim)" }}>
                  {t("partida.minuto", { minuto: evento.minute })}
                </span>
                <span className="label" style={{ minWidth: 34, color: evento.mine ? "var(--primary)" : "var(--text-muted)" }}>
                  {evento.clubShort}
                </span>
                <span style={{ color: evento.kind === "gol" ? "var(--accent)" : "inherit" }}>
                  {lanceDaPartida(evento.key, evento.params)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <Panel title={t("relatorios.estatisticas")}>
        <div className="grid" style={{ gap: 12 }}>
          {estatisticas.map(([rotulo, casa, fora, unidade]) => {
            const valores = [casa, fora];
            const eu = valores[minhas] ?? 0;
            const ele = valores[deles] ?? 0;
            const total = eu + ele || 1;
            return (
              <div key={rotulo}>
                <div className="row" style={{ justifyContent: "space-between", fontSize: 12 }}>
                  <b className="num">{eu}{unidade}</b>
                  <span className="label-muted">{t(rotulo)}</span>
                  <b className="num">{ele}{unidade}</b>
                </div>
                <div className="relatorios-barra-dupla">
                  <i style={{ width: `${(eu / total) * 100}%` }} />
                  <i style={{ width: `${(ele / total) * 100}%` }} />
                </div>
              </div>
            );
          })}
        </div>
        {jogo.factors.length > 0 && (
          <>
            <h3 className="label" style={{ margin: "16px 0 6px" }}>
              {t("partida.porQueAconteceu")}
            </h3>
            <ul className="muted" style={{ margin: 0, paddingLeft: 18, fontSize: 12.5 }}>
              {jogo.factors.slice(0, 3).map((fator, indice) => (
                <li key={indice} style={{ marginBottom: 4 }}>
                  {fatorDaPartida(fator.key, fator.params)}
                </li>
              ))}
            </ul>
          </>
        )}
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rodada
// ---------------------------------------------------------------------------

function Rodada({ view, onOpenPlayer }: { view: ClubView; onOpenPlayer: (id: string) => void }): ReactNode {
  const t = useT();
  const artilheiros = view.scorers.slice(0, 8);
  const ultimo = view.calendar.filter((jogo) => jogo.played).at(-1);
  const { nome, linhas } = tabelaDaCompeticao(view, ultimo?.competition);
  return (
    <div className="relatorios-duas">
      <StandingsTable title={nome} rows={linhas} maxHeight="calc(100vh - 320px)" />
      <div className="grid" style={{ gap: 14, alignContent: "start" }}>
        <Panel title={t("competicoes.abaArtilharia")}>
          {artilheiros.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>{t("comum.vazio")}</p>
          ) : (
            <div className="adm-pares">
              {artilheiros.map((linha) => (
                <button key={linha.playerId} className="relatorios-linha" onClick={() => onOpenPlayer(linha.playerId)}>
                  <span className="corta">
                    <b>{linha.name}</b> <span className="muted">· {linha.clubShort}</span>
                  </span>
                  <span className="num">{linha.goals}</span>
                </button>
              ))}
            </div>
          )}
        </Panel>
        {view.jornal.roundTeam.length > 0 && (
          <Panel title={t("relatorios.selecaoDaRodada")}>
            <div className="adm-pares">
              {view.jornal.roundTeam.map((linha) => (
                <button key={linha.playerId} className="relatorios-linha" onClick={() => onOpenPlayer(linha.playerId)}>
                  <span className="corta">
                    <span className="label-muted" style={{ marginRight: 6 }}>{linha.line}</span>
                    <b>{linha.name}</b> <span className="muted">· {linha.clube.short}</span>
                  </span>
                  <span className="num">{linha.rating.toFixed(1)}</span>
                </button>
              ))}
            </div>
          </Panel>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Elenco
// ---------------------------------------------------------------------------

function Elenco({ view, onOpenPlayer }: { view: ClubView; onOpenPlayer: (id: string) => void }): ReactNode {
  const t = useT();
  const jogaram = view.squad.filter((jogador) => jogador.apps > 0);
  const destaques = [...jogaram].sort((a, b) => b.rating - a.rating).slice(0, 4);
  const emQueda = [...view.squad].filter((jogador) => jogador.morale < 55).sort((a, b) => a.morale - b.morale).slice(0, 4);
  const fisico = view.squad
    .filter((jogador) => jogador.injuryRounds > 0 || jogador.suspensionRounds > 0 || jogador.condition < 70)
    .sort((a, b) => b.injuryRounds - a.injuryRounds || a.condition - b.condition);
  const insatisfeitos = view.squad.filter((jogador) => jogador.morale < 45).length;

  const linha = (jogador: PlayerRow, direita: ReactNode) => (
    <button key={jogador.id} className="relatorios-linha" onClick={() => onOpenPlayer(jogador.id)}>
      <span className="row corta" style={{ gap: 8 }}>
        <PositionChip pos={jogador.pos} />
        <b className="corta">{jogador.name}</b>
      </span>
      {direita}
    </button>
  );

  return (
    <div className="relatorios-duas">
      <div className="grid" style={{ gap: 14, alignContent: "start" }}>
        <Panel title={t("relatorios.destaques")}>
          <div className="adm-pares">
            {destaques.length === 0 ? (
              <span className="muted">{t("comum.vazio")}</span>
            ) : (
              destaques.map((jogador) => linha(jogador, <span className="num" style={{ color: "var(--primary)" }}>{jogador.rating.toFixed(1)}</span>))
            )}
          </div>
        </Panel>
        <Panel title={t("relatorios.emQueda")}>
          <div className="adm-pares">
            {emQueda.length === 0 ? (
              <span className="muted">{t("comum.vazio")}</span>
            ) : (
              emQueda.map((jogador) => linha(jogador, <MoraleFace value={jogador.morale} size={15} />))
            )}
          </div>
        </Panel>
      </div>
      <div className="grid" style={{ gap: 14, alignContent: "start" }}>
        <Panel title={t("relatorios.fisico")}>
          <div className="adm-pares">
            {fisico.length === 0 ? (
              <span className="muted">{t("comum.vazio")}</span>
            ) : (
              fisico.map((jogador) =>
                linha(
                  jogador,
                  jogador.injuryRounds > 0 ? (
                    <span style={{ color: "var(--danger)" }}>{t("elenco.lesionado", { rodadas: jogador.injuryRounds })}</span>
                  ) : jogador.suspensionRounds > 0 ? (
                    <span style={{ color: "var(--warning)" }}>{t("elenco.suspenso")}</span>
                  ) : (
                    <span className="num" style={{ color: "var(--warning)" }}>{jogador.condition}%</span>
                  ),
                ),
              )
            )}
          </div>
        </Panel>
        <Panel title={t("relatorios.moral")}>
          <div className="adm-pares">
            <div>
              <span className="muted">{t("relatorios.moralMedia")}</span>
              <span className="row" style={{ gap: 5 }}>
                <MoraleFace value={view.club.moraleAverage} size={14} />
                <b className="num">{view.club.moraleAverage}</b>
              </span>
            </div>
            <div>
              <span className="muted">{t("relatorios.insatisfeitos")}</span>
              <b className="num">{insatisfeitos}</b>
            </div>
            <div>
              <span className="muted">{t("relatorios.forcaMedia")}</span>
              <StrengthBadge value={Math.round(view.squad.reduce((soma, j) => soma + j.strength, 0) / Math.max(1, view.squad.length))} />
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Financeiro
// ---------------------------------------------------------------------------

function Financeiro({ view }: { view: ClubView }): ReactNode {
  const t = useT();
  const { finance } = view;
  /* Duas escalas: o acumulado da temporada e a conta de uma rodada. Numa escala
     só, a folha de uma rodada aparecia do tamanho da bilheteria do ano. */
  const temporada: Array<[Chave, number]> = [
    ["relatorios.bilheteria", finance.gate],
    ["relatorios.patrocinio", finance.sponsor],
    ["relatorios.tv", finance.tv],
    ["relatorios.premiacao", finance.prizes],
  ];
  const outras = finance.expensePerRound === null ? 0 : Math.max(0, finance.expensePerRound - finance.wagesPerRound);
  const rodada: Array<[Chave, number, string]> = [
    ["relatorios.receita", finance.incomePerRound ?? 0, "var(--primary)"],
    ["relatorios.folha", finance.wagesPerRound, "var(--danger)"],
    ["relatorios.outrasDespesas", outras, "var(--danger)"],
  ];
  const maiorNaTemporada = Math.max(1, ...temporada.map(([, v]) => v));
  const maiorNaRodada = Math.max(1, ...rodada.map(([, v]) => v));
  const saldo =
    finance.incomePerRound === null || finance.expensePerRound === null ? null : finance.incomePerRound - finance.expensePerRound;

  const barra = (rotulo: Chave, valor: number, maior: number, cor: string) => (
    <div key={rotulo} className="relatorios-fluxo">
      <span className="muted">{t(rotulo)}</span>
      <div className="relatorios-barra">
        <i style={{ width: `${(valor / maior) * 100}%`, background: cor }} />
      </div>
      <Money value={valor} />
    </div>
  );

  return (
    <div className="relatorios-duas">
      <Panel title={t("relatorios.entradas")}>
        {temporada.map(([rotulo, valor]) => barra(rotulo, valor, maiorNaTemporada, "var(--primary)"))}
      </Panel>
      <Panel title={t("relatorios.saidas")}>
        {rodada.map(([rotulo, valor, cor]) => barra(rotulo, valor, maiorNaRodada, cor))}
        <div className="row" style={{ justifyContent: "space-between", marginTop: 14 }}>
          <span className="muted">{t("financas.resultadoPorRodada")}</span>
          {saldo === null ? <span className="muted">{t("comum.vazio")}</span> : <Money value={saldo} strong />}
        </div>
      </Panel>
    </div>
  );
}
